import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Send, Users, FileSpreadsheet, Upload, X, AlertCircle, CheckCircle2, Info } from 'lucide-react'
import { toast } from 'sonner'
import { whatsappApi } from '../api/whatsapp'
import { WhatsAppPreview } from '../components/WhatsAppPreview'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Plantilla, EnvioFormData, ExcelProcessResult } from '../types'

export function EnvioMasivoWhatsApp() {
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [plantillas, setPlantillas] = useState<Plantilla[]>([])
  const [plantillaSeleccionada, setPlantillaSeleccionada] = useState<Plantilla | null>(null)
  const [formData, setFormData] = useState<EnvioFormData>({
    asunto: '',
    mensaje: '',
    destinatarios: '',
    plantilla_id: null,
    variables_personalizadas: {},
    variables_individuales: []
  })
  const [preview, setPreview] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [excelFile, setExcelFile] = useState<File | null>(null)
  const [excelResult, setExcelResult] = useState<ExcelProcessResult | null>(null)
  const [procesandoExcel, setProcesandoExcel] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  useEffect(() => {
    cargarPlantillas()
  }, [])

  const cargarPlantillas = async () => {
    try {
      const data = await whatsappApi.getPlantillas(true)
      setPlantillas(data)
    } catch (error) {
      console.error('Error cargando plantillas:', error)
      toast.error('Error al cargar las plantillas')
    }
  }

  const handlePlantillaChange = async (plantillaId: string) => {
    if (!plantillaId || plantillaId === 'none') {
      setPlantillaSeleccionada(null)
      setFormData(prev => ({
        ...prev,
        plantilla_id: null,
        mensaje: '',
        variables_personalizadas: {}
      }))
      setPreview('')
      return
    }

    try {
      const plantilla = await whatsappApi.getPlantilla(parseInt(plantillaId))
      setPlantillaSeleccionada(plantilla)
      setFormData(prev => ({
        ...prev,
        plantilla_id: plantilla.id,
        mensaje: plantilla.contenido,
        variables_personalizadas: plantilla.variables_ejemplo || {}
      }))
      actualizarPreview(plantilla.contenido, plantilla.variables_ejemplo || {})

      // Re-process Excel if one is uploaded to get variables for the new template
      if (excelFile) {
        setProcesandoExcel(true)
        try {
          const result = await whatsappApi.procesarExcel(excelFile, plantilla.id)
          setExcelResult(result)
          setFormData(prev => ({
            ...prev,
            destinatarios: result.numeros_texto,
            variables_individuales: result.variables_individuales
          }))
        } catch (error) {
          console.error('Error re-procesando Excel:', error)
        } finally {
          setProcesandoExcel(false)
        }
      }
    } catch (error) {
      console.error('Error cargando plantilla:', error)
      toast.error('Error al cargar la plantilla')
    }
  }

  const actualizarPreview = (contenido: string, variables: Record<string, string>) => {
    let texto = contenido
    Object.entries(variables).forEach(([key, value]) => {
      texto = texto.replace(new RegExp(`{{${key}}}`, 'g'), value || `{{${key}}}`)
    })
    setPreview(texto)
  }

  const handleVariableChange = (variable: string, valor: string) => {
    const nuevasVariables = {
      ...formData.variables_personalizadas,
      [variable]: valor
    }
    setFormData({ ...formData, variables_personalizadas: nuevasVariables })
    if (plantillaSeleccionada) {
      actualizarPreview(plantillaSeleccionada.contenido, nuevasVariables)
    }
  }

  const handleMensajeChange = (mensaje: string) => {
    setFormData({ ...formData, mensaje })
    if (!plantillaSeleccionada) {
      setPreview(mensaje)
    }
  }

  const contarDestinatarios = () => {
    return formData.destinatarios.split(',').filter(n => n.trim()).length
  }

  const handleExcelUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('El archivo debe ser formato Excel (.xlsx o .xls)')
      return
    }

    setExcelFile(file)
    setProcesandoExcel(true)

    try {
      const result = await whatsappApi.procesarExcel(file, formData.plantilla_id || undefined)
      setExcelResult(result)
      setFormData(prev => ({
        ...prev,
        destinatarios: result.numeros_texto,
        variables_individuales: result.variables_individuales
      }))
      toast.success(`Excel procesado: ${result.validos} números válidos`)
    } catch (error: any) {
      console.error('Error procesando Excel:', error)
      toast.error(error.response?.data?.message || 'Error al procesar el archivo Excel')
      setExcelFile(null)
      setExcelResult(null)
    } finally {
      setProcesandoExcel(false)
    }
  }

  const handleRemoveExcel = () => {
    setExcelFile(null)
    setExcelResult(null)
    setFormData(prev => ({
      ...prev,
      destinatarios: '',
      variables_individuales: []
    }))
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.asunto || !formData.mensaje || !formData.destinatarios) {
      toast.error('Completa todos los campos requeridos')
      return
    }

    const count = contarDestinatarios()
    if (count === 0) {
      toast.error('Debes agregar al menos un destinatario')
      return
    }

    if (!confirm(`¿Enviar mensaje a ${count} destinatario(s)?`)) {
      return
    }

    setEnviando(true)

    try {
      const response = await whatsappApi.enviarMensajes(formData)
      toast.success(response.mensaje || `Envío iniciado. ID: ${response.envio_id}`)
      // Redirigir al historial después de un breve delay
      setTimeout(() => {
        navigate('/whatsapp/historial')
      }, 1500)
    } catch (error: any) {
      console.error('Error al enviar mensajes:', error)
      toast.error(error.response?.data?.error || 'Error al enviar mensajes')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Send className="h-6 w-6 text-black dark:text-white" />
          Envío Masivo de WhatsApp
        </h1>
        <button
          type="button"
          onClick={() => setShowInfo(!showInfo)}
          className="text-muted-foreground hover:text-foreground transition-colors ml-2"
        >
          <Info className="h-5 w-5" />
        </button>
        {showInfo && (
          <span className="text-muted-foreground ml-2 text-sm animate-in slide-in-from-left-2 duration-200">
            Envía mensajes masivos usando plantillas aprobadas
          </span>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardContent className="pt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="asunto">Asunto del Envío *</Label>
                  <Input
                    id="asunto"
                    value={formData.asunto}
                    onChange={(e) => setFormData({ ...formData, asunto: e.target.value })}
                    placeholder="Campaña de Verano 2025"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="plantilla">Plantilla</Label>
                  <Select
                    value={plantillaSeleccionada?.id.toString() || 'none'}
                    onValueChange={handlePlantillaChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Mensaje libre (sin plantilla)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Mensaje libre (sin plantilla)</SelectItem>
                      {plantillas.map((p) => (
                        <SelectItem key={p.id} value={p.id.toString()}>
                          {p.nombre} - Twilio
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {plantillaSeleccionada && Object.keys(plantillaSeleccionada.variables || {}).length > 0 && (
                  <Card className="border border-gray-300 dark:border-gray-700">
                    <CardHeader>
                      <CardTitle className="text-sm">Variables de la Plantilla</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {Object.entries(plantillaSeleccionada.variables || {}).map(([key, _value]) => (
                        <div key={key} className="space-y-2">
                          <Label htmlFor={key}>{key}</Label>
                          <Input
                            id={key}
                            value={formData.variables_personalizadas[key] || ''}
                            onChange={(e) => handleVariableChange(key, e.target.value)}
                            placeholder={plantillaSeleccionada.variables_ejemplo?.[key] || ''}
                          />
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <div className="space-y-2">
                  <Label htmlFor="mensaje">Mensaje *</Label>
                  <Textarea
                    id="mensaje"
                    rows={6}
                    value={formData.mensaje}
                    onChange={(e) => handleMensajeChange(e.target.value)}
                    placeholder="Escribe tu mensaje aquí..."
                    required
                    disabled={!!plantillaSeleccionada}
                  />
                </div>

                {/* Sección de Carga de Excel */}
                <Card className="border border-gray-300 dark:border-gray-700 bg-teal-500/10 dark:bg-teal-800/20">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileSpreadsheet className="h-4 w-4 text-teal-500" />
                      Cargar Destinatarios desde Excel
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {!excelFile ? (
                      <div
                        className="border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-6 text-center cursor-pointer hover:bg-teal-500/5 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="h-8 w-8 mx-auto text-gray-500 mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Haz clic para seleccionar un archivo Excel
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          El archivo debe tener una columna "numero"
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          onChange={handleExcelUpload}
                          className="hidden"
                        />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div className="flex items-center gap-2">
                            <FileSpreadsheet className="h-5 w-5 text-teal-500" />
                            <div>
                              <p className="text-sm font-medium">{excelFile.name}</p>
                              {procesandoExcel && (
                                <p className="text-xs text-muted-foreground">Procesando...</p>
                              )}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveExcel}
                            disabled={procesandoExcel}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        {excelResult && (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <Badge className="badge-gradient hover:opacity-90">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                {excelResult.validos} válidos
                              </Badge>
                              {excelResult.invalidos > 0 && (
                                <Badge variant="secondary" className="bg-red-100 text-red-700">
                                  <AlertCircle className="h-3 w-3 mr-1" />
                                  {excelResult.invalidos} inválidos
                                </Badge>
                              )}
                            </div>

                            {excelResult.errores.length > 0 && (
                              <Alert variant="destructive" className="py-2">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-xs">
                                  <ul className="list-disc list-inside max-h-20 overflow-y-auto">
                                    {excelResult.errores.slice(0, 5).map((error, idx) => (
                                      <li key={idx}>{error}</li>
                                    ))}
                                    {excelResult.errores.length > 5 && (
                                      <li>...y {excelResult.errores.length - 5} más</li>
                                    )}
                                  </ul>
                                </AlertDescription>
                              </Alert>
                            )}

                            {excelResult.variables_esperadas.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                <span className="font-medium">Variables detectadas: </span>
                                {excelResult.columnas_detectadas.join(', ')}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">
                      O ingresa manualmente
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="destinatarios">
                    Destinatarios * (separados por comas)
                  </Label>
                  <Textarea
                    id="destinatarios"
                    rows={4}
                    value={formData.destinatarios}
                    onChange={(e) => setFormData({ ...formData, destinatarios: e.target.value, variables_individuales: [] })}
                    placeholder="+593990123456, +593990123457, ..."
                    required
                    disabled={!!excelFile}
                  />
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    Total: {contarDestinatarios()} destinatarios
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-teal-800 hover:bg-teal-900 text-white"
                  size="lg"
                  disabled={enviando}
                >
                  {enviando ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Enviar Mensajes
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <WhatsAppPreview mensaje={preview || formData.mensaje} />
          </div>
        </div>
      </form>
    </div>
  )
}
